import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stack, Button, TextField, InputAdornment, CircularProgress,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Inventory as InventoryIcon,
  Warning as WarningIcon, CheckCircle as InStockIcon, Cancel as OutOfStockIcon,
  Save as SaveIcon, LocalAtm as ValueIcon, Layers as UnitsIcon,
} from '@mui/icons-material';
import api from '../api/axios';
import { color } from '../theme/tokens';
import {
  PageHeader, Panel, DataTable, Toolbar, StatusBadge, MetricCard, useFeedback,
} from '../components/ui';

const fmtINR = (v) => '₹' + Number(v || 0).toLocaleString('en-IN');
const fmtNum = (v) => Number(v || 0).toLocaleString('en-IN');

const stockBadge = (qty) => {
  if (qty === 0) return <StatusBadge tone="danger" label="Out of stock" />;
  if (qty <= 10) return <StatusBadge tone="warning" label={`Low: ${qty}`} />;
  return <StatusBadge tone="success" label={qty} />;
};

const InventoryV2 = () => {
  const { success, error } = useFeedback();
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('stock-asc');
  const [editingId, setEditingId] = useState(null);
  const [editStock, setEditStock] = useState('');
  const [savingId, setSavingId] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/v2/products/admin/inventory', { params: { low_threshold: 10 } });
      setSummary(res.data.summary);
    } catch (e) {
      error('Failed to load inventory summary');
    } finally {
      setLoading(false);
    }
  }, [error]);

  const fetchProducts = useCallback(async () => {
    try {
      setListLoading(true);
      const params = { sort, limit: 100 };
      if (search) params.search = search;
      if (category) params.category = category;

      const res = await api.get('/api/v2/products/admin/all', { params });
      setProducts(res.data.data || []);
    } catch (e) {
      error('Failed to load products');
    } finally {
      setListLoading(false);
    }
  }, [search, category, sort, error]);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/api/v2/categories/flat');
      setCategories(res.data.data || []);
    } catch {}
  };

  useEffect(() => {
    fetchSummary();
    fetchProducts();
    fetchCategories();
  }, [fetchSummary, fetchProducts]);

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditStock(String(p.stock_qty));
  };

  const saveStock = async (p) => {
    const val = parseInt(editStock, 10);
    if (isNaN(val) || val < 0) {
      error('Stock must be a non-negative number');
      return;
    }
    try {
      setSavingId(p.id);
      await api.put(`/api/v2/products/${p.id}`, { stock_qty: val });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, stock_qty: val } : x)));
      setEditingId(null);
      setSavingId(null);
      fetchSummary();
      success('Stock updated');
    } catch (e) {
      error('Failed to update stock');
      setSavingId(null);
    }
  };

  const metrics = [
    { key: 'total_products', label: 'Total products', icon: <InventoryIcon />, value: fmtNum(summary?.total_products) },
    { key: 'in_stock', label: 'In stock', icon: <InStockIcon />, value: fmtNum(summary?.in_stock) },
    { key: 'low_stock', label: 'Low stock', icon: <WarningIcon />, value: fmtNum(summary?.low_stock) },
    { key: 'out_of_stock', label: 'Out of stock', icon: <OutOfStockIcon />, value: fmtNum(summary?.out_of_stock) },
    { key: 'total_units', label: 'Total units', icon: <UnitsIcon />, value: fmtNum(summary?.total_units) },
    { key: 'total_stock_value', label: 'Stock value', icon: <ValueIcon />, value: fmtINR(summary?.total_stock_value) },
  ];

  const columns = [
    {
      key: 'name',
      label: 'Product',
      sx: { minWidth: 240 },
      render: (v, row) => (
        <Box>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: color.ink[900] }}>{v}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: color.ink[500] }}>
            {row.item_code || row.sku || `#${row.id}`}
          </Typography>
        </Box>
      ),
    },
    { key: 'category_name', label: 'Category', render: (v) => v || '—' },
    { key: 'mrp', label: 'MRP', align: 'right', render: (v) => fmtINR(v) },
    {
      key: 'sale_price',
      label: 'Sale price',
      align: 'right',
      render: (v) => (
        <Typography component="span" sx={{ fontWeight: 600, color: color.ink[900] }}>{fmtINR(v)}</Typography>
      ),
    },
    {
      key: 'stock_qty',
      label: 'Stock',
      align: 'center',
      sx: { minWidth: 220 },
      render: (v, row) => (
        editingId === row.id ? (
          <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
            <TextField
              size="small"
              type="number"
              value={editStock}
              onChange={(e) => setEditStock(e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">units</InputAdornment> }}
              sx={{ width: 130 }}
              autoFocus
            />
            <Button
              size="small"
              variant="contained"
              onClick={() => saveStock(row)}
              disabled={savingId === row.id}
              startIcon={savingId === row.id ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            >
              Save
            </Button>
            <Button
              size="small"
              color="inherit"
              onClick={() => setEditingId(null)}
              disabled={savingId === row.id}
            >
              Cancel
            </Button>
          </Stack>
        ) : stockBadge(v)
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'center',
      width: 120,
      render: (_, row) => (
        editingId === row.id ? null : (
          <Button size="small" onClick={() => startEdit(row)}>Edit stock</Button>
        )
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Catalog"
        title="Inventory"
        subtitle="Stock levels and low-stock alerts"
        actions={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchSummary(); fetchProducts(); }}
            disabled={loading || listLoading}
          >
            Refresh
          </Button>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(6, 1fr)',
          },
          gap: 2,
          mb: 3,
        }}
      >
        {metrics.map((m) => (
          <MetricCard key={m.key} label={m.label} value={m.value} icon={m.icon} loading={loading} />
        ))}
      </Box>

      <Panel dense sx={{ mb: 2 }}>
        <Toolbar
          search={search}
          onSearch={setSearch}
          searchPlaceholder="Search by name, code..."
          onRefresh={() => { fetchSummary(); fetchProducts(); }}
          refreshing={listLoading}
        >
          <Stack direction="row" spacing={1.5}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                label="Category"
                onChange={e => setCategory(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sort}
                label="Sort By"
                onChange={e => setSort(e.target.value)}
              >
                <MenuItem value="stock-asc">Stock (Lowest first)</MenuItem>
                <MenuItem value="stock-desc">Stock (Highest first)</MenuItem>
                <MenuItem value="price-asc">Price (Low-High)</MenuItem>
                <MenuItem value="price-desc">Price (High-Low)</MenuItem>
                <MenuItem value="name-asc">Name (A-Z)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Toolbar>
      </Panel>

      <Panel
        title="Stock management"
        subtitle="Manage product stock levels and view inventory metrics"
        noPadding
      >
        <DataTable
          columns={columns}
          rows={products}
          rowKey="id"
          loading={listLoading}
          skeletonRows={6}
          size="small"
          minWidth={900}
          emptyTitle="No products found"
          emptyDescription="There are no products to display for this inventory view."
          sx={{ border: 0, borderRadius: 0, boxShadow: 'none' }}
        />
      </Panel>
    </Box>
  );
};

export default InventoryV2;
